import { EventEmitterInterface } from '../event/emitter.interface';
import { WindowServiceInterface } from '../../service/window/service.interface';
import { easingFunctionHelperType } from '../../helper/easing/types';
import { easingLinear } from '../../helper/easing/linear';
import { BrowserWindowEvent } from './window.event';

/**
 * Wrap the window object
 */
export class BrowserWindow implements WindowServiceInterface {
  /**
   * Frames per second
   */
  private readonly fps: number = 60;

  /**
   * Duration of frame in millisecond
   */
  private readonly frameMs: number = (1 / this.fps) * 1000;

  /**
   * Window inner height
   */
  private innerHeight: number = null;

  /**
   * Window inner width
   */
  private innerWidth: number = null;

  /**
   * Create instance of BrowserWindow
   *
   * @param eventEmitter
   * @param window
   */
  constructor(private eventEmitter: EventEmitterInterface, private window: Window) {
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public observers(): void {
    this.window.addEventListener(BrowserWindowEvent.load, this.onLoadWindow);
    this.window.addEventListener(BrowserWindowEvent.resize, this.onResizeWindow);
    this.window.addEventListener(BrowserWindowEvent.scroll, this.onScrollWindow);
    this.window.addEventListener(BrowserWindowEvent.popstate, this.onPopstateWindow);
    this.window.addEventListener(BrowserWindowEvent.keyup, this.onKeyupWindow);
    this.window.addEventListener(BrowserWindowEvent.keydown, this.onKeydownWindow);
    this.window.addEventListener(BrowserWindowEvent.click, this.onClickWindow);
  }

  /**
   * @inheritDoc
   */
  public getNative(): Window {
    return this.window;
  }

  /**
   * @inheritdoc
   */
  public getScrollTop(): number {
    return this.window.scrollY || this.window.pageYOffset || this.window.document.documentElement.scrollTop;
  }

  /**
   * @inheritDoc
   */
  public setTimeout(fn: () => void, delay: number): number {
    return this.window.setTimeout(fn, delay);
  }

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * @inheritdoc
   */
  public getInnerHeight(): number {
    if (!this.innerHeight) {
      this.setInnerHeight();
    }

    return this.innerHeight;
  }

  /**
   * @inheritdoc
   */
  public getInnerWidth(): number {
    if (!this.innerWidth) {
      this.setInnerWidth();
    }

    return this.innerWidth;
  }

  /**
   * @inheritDoc
   */
  public scrollToY(
    position: number = 0,
    duration: number = 2000,
    easing: easingFunctionHelperType = easingLinear
  ): void {
    const scrollY = this.window.scrollY || this.window.pageYOffset || this.window.document.documentElement.scrollTop;

    const currentTime = 0;

    if (position === scrollY) {
      return;
    }

    this.updateScrollToY(position, duration, easing, currentTime);
  }

  /**
   * Sets window inner height
   */
  private setInnerHeight(): void {
    this.innerHeight = this.getNative().innerHeight;
  }

  /**
   * Sets window inner width
   */
  private setInnerWidth(): void {
    this.innerWidth = this.getNative().innerWidth;
  }

  /**
   * Update scrollToY, update a position of the scrollY
   * @param position
   * @param duration
   * @param easing
   * @param currentTime
   */
  private updateScrollToY(
    position: number,
    duration: number,
    easing: easingFunctionHelperType,
    currentTime: number
  ): void {
    currentTime += this.frameMs;

    const currentPosition = currentTime / duration;
    const transitionPosition = easing(currentPosition);

    const scrollY = this.window.scrollY || this.window.pageYOffset || this.window.document.documentElement.scrollTop;

    this.window.scrollTo(0, scrollY + (position - scrollY) * transitionPosition);

    if (currentPosition < 1) {
      this.window.requestAnimationFrame(() => {
        this.updateScrollToY(position, duration, easing, currentTime);
      });
    }
  }

  /**
   * Emit an event when the window loads
   */
  private onLoadWindow = (): void => {
    this.getEventEmitter().emit(BrowserWindowEvent.load);
  };

  /**
   * Emit an event when the window resizes
   */
  private onResizeWindow = (): void => {
    this.setInnerHeight();
    this.setInnerWidth();
    this.getEventEmitter().emit(BrowserWindowEvent.resize);
  };

  /**
   * Emit an event when the window scrolls
   */
  private onScrollWindow = (): void => {
    this.getEventEmitter().emit(BrowserWindowEvent.scroll);
  };

  /**
   * Emit an event when the window's history changes
   */
  private onPopstateWindow = (): void => {
    this.getEventEmitter().emit(BrowserWindowEvent.popstate);
  };

  /**
   * Emit an event when the window's keyup event fires
   */
  private onKeyupWindow = (e: KeyboardEvent): void => {
    this.getEventEmitter().emit(BrowserWindowEvent.keyup, e);
  };

  /**
   * Emit an event when the window's keydown event fires
   */
  private onKeydownWindow = (e: KeyboardEvent): void => {
    this.getEventEmitter().emit(BrowserWindowEvent.keydown, e);
  };

  /**
   * Emit an event when the window's click event fires
   */
  private onClickWindow = (e: KeyboardEvent): void => {
    this.getEventEmitter().emit(BrowserWindowEvent.click, e);
  };
}
